import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, List, ListItem, ListItemAvatar, Avatar,
  ListItemText, CircularProgress, Box, Checkbox, FormControlLabel,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem
} from "@mui/material";

// Debounce helper
const useDebounce = (fn, delay = 300) => {
  const t = useRef(null);
  return (...args) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onPick?: (card) => void     // optional: when you just want to return a picked card
 *  - onAdd?:  (card) => Promise|void  // called when user presses "Add"; keep dialog open
 */
export default function CardLookupDialog({ open, onClose, onPick, onAdd, initialQuery = "", initialSearchHints = null }) {
  const [q, setQ] = useState("");
  const [highOnly, setHighOnly] = useState(true);
  const DEFAULT_SERIES = "";
  const MEGA_EVOLUTION_SERIES = "Mega Evolution";
  const [seriesRadio, setSeriesRadio] = useState(DEFAULT_SERIES);
  const [otherSeries, setOtherSeries] = useState("__ALL__");
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchHints, setSearchHints] = useState(null);
  const inputRef = useRef(null);

  const getEffectiveSeries = (radioVal, otherVal) => {
    // If dropdown is used:
    //  - "__ALL__" means no series filter
    //  - any other non-empty value overrides radio
    if (otherVal === "__ALL__") return "";
    if (otherVal) return otherVal;
    return radioVal || "";
  };

  const fetchResults = async (term, highOnlyFlag, seriesName, hints = null) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const buildLookupUrl = (searchTerm, searchHints = null) => {
        const seriesParam = seriesName
          ? `&series_name=${encodeURIComponent(seriesName)}`
          : "";
        const hintParams = new URLSearchParams();
        if (searchHints?.guessed_name) hintParams.set("guessed_name", searchHints.guessed_name);
        if (searchHints?.guessed_number) hintParams.set("guessed_number", searchHints.guessed_number);
        if (searchHints?.guessed_rarity) hintParams.set("guessed_rarity", searchHints.guessed_rarity);
        if (searchHints?.guessed_set_name) hintParams.set("guessed_set_name", searchHints.guessed_set_name);
        if (searchHints?.guessed_series_name) hintParams.set("guessed_series_name", searchHints.guessed_series_name);
        const extraParams = hintParams.toString() ? `&${hintParams.toString()}` : "";
        return `/api/cards/lookup?q=${encodeURIComponent(searchTerm)}&limit=24&high_only=${
          highOnlyFlag ? "1" : "0"
        }${seriesParam}${extraParams}`;
      };

      const parseResults = async (searchTerm, searchHints) => {
        const res = await fetch(buildLookupUrl(searchTerm, searchHints));
        const json = await res.json();
        return Array.isArray(json?.data) ? json.data : [];
      };

      let nextResults = await parseResults(term, hints);
      const fallbackName = (hints?.guessed_name || "").trim();
      if (!nextResults.length && fallbackName && fallbackName.length >= 2 && fallbackName.toLowerCase() !== term.trim().toLowerCase()) {
        nextResults = await parseResults(fallbackName, {
          guessed_name: fallbackName,
          guessed_number: "",
          guessed_rarity: "",
          guessed_set_name: "",
          guessed_series_name: "",
        });
      }
      setResults(nextResults);
    } catch (e) {
      console.error(e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useDebounce(fetchResults, 300);

  useEffect(() => {
    if (!open) return;
    const nextQuery = (initialQuery || "").trim();
    setQ(nextQuery);
    const nextHints = initialSearchHints || null;
    setSearchHints(nextHints);
    if (nextQuery.length >= 2) {
      const effective = getEffectiveSeries(seriesRadio, otherSeries);
      fetchResults(nextQuery, highOnly, effective, nextHints);
    } else {
      setResults([]);
    }
    setTimeout(() => inputRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  // Load available series for dropdown
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch("/api/cards/lookup/series");
        const json = await res.json();
        const items = Array.isArray(json?.data) ? json.data : [];
        // Exclude the two radio series to keep the dropdown focused on "other" series
        const filtered = items.filter(
          (s) => s && s !== DEFAULT_SERIES && s !== MEGA_EVOLUTION_SERIES
        );
        setSeriesOptions(filtered);
      } catch (e) {
        console.error(e);
        setSeriesOptions([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetAndFocus = () => {
    setQ("");
    setResults([]);
    // re-focus the text field so you can type the next one
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleClose = () => {
    resetAndFocus();
    // Reset series selector to defaults when leaving the modal
    setSeriesRadio(DEFAULT_SERIES);
    setOtherSeries("__ALL__");
    onClose?.();
  };

  const handleAdd = async (card) => {
    try {
      // allow parent to add to session (can be async)
      await onAdd?.(card);
    } finally {
      // clear and keep dialog open for the next search
      resetAndFocus();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Find Card</DialogTitle>
      <DialogContent dividers>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 1 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl component="fieldset" sx={{ minWidth: 320 }}>
            <FormLabel component="legend">Series</FormLabel>
            <RadioGroup
              row
              value={seriesRadio}
              onChange={(e) => {
                const nextRadio = e.target.value;
                setSeriesRadio(nextRadio);
                const effective = getEffectiveSeries(nextRadio, otherSeries);
                debouncedFetch(q, highOnly, effective, searchHints);
              }}
            >
              <FormControlLabel
                value="Scarlet & Violet"
                control={<Radio />}
                label="Scarlet & Violet"
              />
              <FormControlLabel
                value={MEGA_EVOLUTION_SERIES}
                control={<Radio />}
                label="Mega Evolution"
              />
            </RadioGroup>
          </FormControl>

          <FormControl sx={{ minWidth: 260 }}>
            <FormLabel sx={{ mb: 0.5 }}>Other series</FormLabel>
            <Select
              size="small"
              value={otherSeries}
              onChange={(e) => {
                const v = e.target.value;
                setOtherSeries(v);
                const effective = getEffectiveSeries(seriesRadio, v);
                debouncedFetch(q, highOnly, effective, searchHints);
              }}
              displayEmpty
            >
              <MenuItem value="__ALL__">All series</MenuItem>
              <MenuItem value="">
                <em>Use radio selection</em>
              </MenuItem>
              {seriesOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            fullWidth
            inputRef={inputRef}
            autoFocus
            label="Type name / set / number (e.g. 'Bulbasaur 143/142')"
            value={q}
            onChange={(e) => {
              const v = e.target.value;
              setQ(v);
              const effective = getEffectiveSeries(seriesRadio, otherSeries);
              debouncedFetch(v, highOnly, effective, searchHints);
            }}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={highOnly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHighOnly(checked);
                  const effective = getEffectiveSeries(seriesRadio, otherSeries);
                  debouncedFetch(q, checked, effective, searchHints);
                }}
              />
            }
            label="High rarity only"
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense>
            {results.map((r) => (
              <ListItem
                key={r.id}
                sx={{ alignItems: "flex-start" }}
                // Clicking the row (left area) can still "pick" if you use it
                onClick={(e) => {
                  // ignore clicks from the buttons on the right
                  if (e.defaultPrevented) return;
                  onPick?.(r);
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={r.image_url || ""}
                    alt={r.name_and_number}
                    sx={{ width: 96, height: 132, mr: 2, borderRadius: 2 }}
                  />
                </ListItemAvatar>

                <ListItemText
                  primaryTypographyProps={{ fontWeight: "bold" }}
                  primary={r.name_and_number}
                  secondary={`${r.set_name || "–"}${r.rarity ? ` • ${r.rarity}` : ""}`}
                />

                {/* Actions on the right */}
                <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                  {onPick && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.preventDefault();
                        onPick?.(r);
                      }}
                    >
                      Select
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={async (e) => {
                      e.preventDefault();
                      await handleAdd(r);
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
